import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/shared/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/shared/i18n";
import { useImagePreview } from "./useImagePreview";
import { usePresetNavigation } from "./usePresetNavigation";
import { usePackValidation } from "./usePackValidation";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import PackService, { Preset } from "../services/packService";
import { useQueryClient } from "@tanstack/react-query";
import PresetService from "../services/presets/presetService";
import { Pack } from "../services/packs/types";
import TagsService from "@/features/tags/service/tagService";

export const usePackForm = (
  initialData?: Pack, 
  getFormData?: () => {
    name: string;
    description: string;
    creator: string;
    cover: string;
    published: boolean;
    creators: any[];
  },
  selectedTags?: string[]
) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('packs');
  const [presets, setPresets] = useState<Preset[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [published, setPublished] = useState<boolean>(initialData?.published ?? false);
  const [coverFile, setCoverFile] = useState<File | null>(null);

  const {
    imagePreview: coverPreview,
    imageError: coverError,
    handleImageChange: _handleCoverChange,
    setImagePreview: setCoverPreview,
    removeImagePreview: handleRemoveCover,
  } = useImagePreview({
    initialImage: initialData?.cover,
    maxSize: 500,
  });

  const { navigateToNewPreset, navigateToEditPreset } = usePresetNavigation();
  const { validateForm } = usePackValidation();
  const queryClient = useQueryClient();

  // Mutation para vincular tags
  const linkTagMutation = useCustomMutation({
    serviceInstance: TagsService.Instance,
    selectAction: (service) => service.linkTagToPack,
  });

  const createPackMutation = useCustomMutation({
    serviceInstance: PackService.Instance,
    selectAction: (service) => service.createPack,
    onSuccess: async (data) => {
      // Vincular tags selecionadas após criar o pack
      if (data && data.id && selectedTags && selectedTags.length > 0) {
        try {
          for (const tagId of selectedTags) {
            await linkTagMutation.mutateAsync({
              tagId,
              packId: data.id
            });
          }
        } catch (error) {
          console.error('Erro ao vincular tags ao pack:', error);
          toast({
            title: "Aviso",
            description: "Pack criado, mas houve erro ao vincular algumas tags.",
            variant: "destructive",
          });
        }
      }

      queryClient.invalidateQueries({ queryKey: ["get-packs"] });
      setSubmitting(false);
      if (data && data.id) {
        navigate(`/packs/${data.id}`, { replace: true });
      }
    },
    onError: (error) => {
      toast({
        title: t('errorCreating'),
        description: t('errorCreatingDescription'),
        variant: "destructive",
      });
      setSubmitting(false);
    },
  });

  const updatePackMutation = useCustomMutation({
    serviceInstance: PackService.Instance,
    selectAction: (service) => service.updatePack,
    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: ["get-packs"] });
      setSubmitting(false);
    },
    onError: (error) => {
      toast({
        title: t('errorUpdating'),
        description: t('errorUpdatingDescription'),
        variant: "destructive",
      });
      setSubmitting(false);
    },
  });

  const removePresetMutation = useCustomMutation({
    serviceInstance: PresetService.Instance,
    selectAction: (service) => service.removePreset,
  });

  const loadPackPresets = useCallback(async (packId: string) => {
    try {
      const data = await PackService.Instance.getPackPresets.actionBuilder({
        packId,
      })().then((data) => {
        return data.map((preset) => {
          const categories = preset.CategoriesPresets.map((category) => (
            {
              id: category.categoryId,
              name: category.category.name,
            }
          ));
          delete preset.CategoriesPresets;
          return {
            ...preset,
            categories,

          }
        });
      });
      // Mapear para o formato esperado pelo PresetsList (PackFormData["presets"])
      const mapped = (data || []).map((preset) => ({
        id: preset.id,
        name: preset.name,
        image: preset.cover,
        cover: preset.cover,
        lut: preset.lut,
        published: preset.published,
        translations: preset.translations,
        ...preset,
        // Adicione outros campos se necessário
      }));
      setPresets(mapped as unknown as Preset[]);
    } catch (error) {
      toast({
        title: t('errorLoadingPresets'),
        description: t('errorLoadingPresetsDescription'),
        variant: "destructive",
      });
    }
  }, [toast, t]);

  useEffect(() => {
    if (initialData?.id) {
      loadPackPresets(initialData.id);
    }
  }, [initialData?.id, loadPackPresets]);

  useEffect(() => {
    if (initialData?.published !== undefined) {
      setPublished(initialData.published);
    }
  }, [initialData?.published]);

  // Set cover preview when initialData changes
  useEffect(() => {
    if (initialData?.cover) {
      setCoverPreview(initialData.cover);
    }
  }, [initialData?.cover, setCoverPreview]);

  const setError = (field: string, message: string) => {
    setFormErrors((prev) => ({ ...prev, [field]: message }));
    return false;
  };

  const clearErrors = () => {
    setFormErrors({});
  };

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCoverFile(e.target.files[0]);
    }
    // Limpa erro de capa quando uma nova capa é selecionada
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.cover;
      return newErrors;
    });
    // Mantém preview funcionando
    _handleCoverChange(e);
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();
    clearErrors();
    setSubmitting(true);

    // Use form data from function or fallback to DOM
    let name = "";
    let description = "";
    let creator = "";
    let creators: any[] = [];

    if (getFormData) {
      // Use data from function
      const formData = getFormData();
      name = formData.name;
      description = formData.description;
      creator = formData.creator;
      creators = formData.creators;
      // Use current coverPreview from hook
      const currentCover = coverPreview || "";
    } else {
      // Fallback to DOM (for backward compatibility)
      const form = document.querySelector('form');
      if (!form) {
        setSubmitting(false);
        return;
      }

      const formDataFromDOM = new FormData(form);
      name = formDataFromDOM.get("name")?.toString() || "";
      description = formDataFromDOM.get("description")?.toString() || "";
      creator = formDataFromDOM.get("creator")?.toString() || "";

      creators = initialData?.packsCreators.map((c) => ({
        id: c.id,
        percentage: c.percentage,
        displayOrder: c.displayOrder
      })) || [];
    }

    const isValid = validateForm({
      name,
      description,
      creator,
      coverPreview,
      presets,
      setError,
      published,
      creators
    });

    if (!isValid) {
      setSubmitting(false);
      return;
    }

    const packId = initialData?.id || crypto.randomUUID();

    const packData = {
      name,
      published,
      build: 1,
      firebaseId: null,
      displayOrder: initialData?.displayOrder ?? null,
      idApple: null,
      highlightOrder: initialData?.highlightOrder ?? null,
      translations: [
        {
          languageCode: "PT_BR" as const,
          description,
        },
      ],
      creators: creators.map(creator => ({
        creatorId: creator.id,
        percentage: creator.percentage,
        displayOrder: creator.displayOrder
      })),
    };

    if (initialData?.id) {
      // Update
      updatePackMutation.mutate({
        id: packId,
        data: { ...packData, cover: coverFile || undefined },
      });
    } else {
      // Create
      createPackMutation.mutate({ data: { ...packData, cover: coverFile! } });
    }
  };

  const handleAddPreset = () => {
    navigateToNewPreset(initialData?.id, initialData?.name);
  };

  const handleEditPreset = async (preset: Preset) => {
    navigateToEditPreset(preset, initialData?.id, initialData?.name);
  };

  const handleRemovePreset = async (presetId: string) => {
    if (!initialData?.id) return;
    try {
      await removePresetMutation.mutateAsync({ id: presetId });
      setPresets(presets.filter((p) => p.id !== presetId));
    } catch (error) {
      console.error("Error removing preset:", error);
      toast({
        title: t('errorRemovingPreset'),
        description: t('errorRemovingPresetDescription'),
        variant: "destructive",
      });
    }
  };

  const handleRemoveCoverWithErrorClear = () => {
    // Limpa erro de capa quando a capa é removida
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.cover;
      return newErrors;
    });
    handleRemoveCover();
  };

  return {
    coverPreview,
    presets,
    submitting,
    formErrors,
    published,
    setPublished,
    handleCoverChange,
    handleSubmit,
    handleAddPreset,
    handleRemovePreset,
    handleEditPreset,
    handleRemoveCover: handleRemoveCoverWithErrorClear,
    setCoverPreview,
    createPackMutation,
    updatePackMutation,
    removePresetMutation,
  };
};
